import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertTriangle, Mail, Eye, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { fmtBusinessDate, todayInBusinessTZ } from "@/lib/timezone";

interface Conflict {
  id: string;
  booking_date: string;
  booking_time: string;
  duration: string;
  service_name: string;
  user_id: string;
  created_at: string;
  client_name: string;
  email: string | null;
  reasonLabel: string;
}

const toMin = (t: string): number | null => {
  const m = t?.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?$/i);
  if (!m) return null;
  let h = parseInt(m[1], 10);
  const p = m[3]?.toUpperCase();
  if (p === "PM" && h !== 12) h += 12;
  if (p === "AM" && h === 12) h = 0;
  return h * 60 + parseInt(m[2], 10);
};
const hhmm = (v: string | null) => {
  if (!v) return null;
  const [h, m] = v.split(":").map(Number);
  return h * 60 + (m || 0);
};
const durMin = (s: string): number => {
  if (!s) return 0;
  let total = 0;
  for (const m of s.toLowerCase().matchAll(/(\d+(?:\.\d+)?)\s*(?:h|hr|hour)/g)) total += Math.round(parseFloat(m[1]) * 60);
  for (const m of s.toLowerCase().matchAll(/(\d+)\s*(?:m|min|minute)/g)) total += parseInt(m[1], 10);
  return total;
};
const fmtBlock = (v: string | null) => {
  const min = hhmm(v);
  if (min == null) return "";
  const h = Math.floor(min / 60);
  const ap = h >= 12 ? "PM" : "AM";
  return `${((h + 11) % 12) + 1}:${String(min % 60).padStart(2, "0")} ${ap}`;
};

/**
 * Flashing alert bar for the admin dashboard: surfaces upcoming appointments
 * that sit on a blocked date/time so they can be rescheduled.
 */
const BlockedDayConflictsBar = () => {
  const [conflicts, setConflicts] = useState<Conflict[]>([]);
  const [open, setOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [previewFor, setPreviewFor] = useState<Conflict | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [sendingId, setSendingId] = useState<string | null>(null);
  const [sentIds, setSentIds] = useState<string[]>([]);
  const [allowingId, setAllowingId] = useState<string | null>(null);


  const load = useCallback(async () => {
    const today = todayInBusinessTZ();
    const { data: blocks } = await supabase
      .from("blocked_dates")
      .select("blocked_date, all_day, start_time, end_time, reason")
      .gte("blocked_date", today);
    if (!blocks || blocks.length === 0) {
      setConflicts([]);
      return;
    }
    const dates = Array.from(new Set(blocks.map((b) => b.blocked_date)));

    const { data: bookings } = await supabase
      .from("bookings")
      .select("id, booking_date, booking_time, duration, service_name, user_id, created_at").is("deleted_at", null)
      .in("booking_date", dates)
      .eq("blocked_day_override", false)
      .neq("status", "cancelled");
    if (!bookings || bookings.length === 0) {
      setConflicts([]);
      return;
    }

    const hits: Array<{ b: any; reasonLabel: string }> = [];
    bookings.forEach((b) => {
      const start = toMin(b.booking_time);
      const end = start != null ? start + Math.max(durMin(b.duration), 1) : null;
      const dayBlocks = blocks.filter((x) => x.blocked_date === b.booking_date);
      const allDay = dayBlocks.find((x) => x.all_day);
      if (allDay) {
        hits.push({ b, reasonLabel: "Closed all day" });
        return;
      }
      const partial = dayBlocks.find((x) => {
        const bs = hhmm(x.start_time);
        const be = hhmm(x.end_time);
        if (bs == null || be == null || start == null || end == null) return false;
        return start < be && end > bs;
      });
      if (partial) {
        hits.push({ b, reasonLabel: `Blocked ${fmtBlock(partial.start_time)} – ${fmtBlock(partial.end_time)}` });
      }
    });

    if (hits.length === 0) {
      setConflicts([]);
      return;
    }

    const userIds = Array.from(new Set(hits.map((h) => h.b.user_id)));
    const { data: profs } = await supabase
      .from("profiles")
      .select("user_id, full_name, email")
      .in("user_id", userIds);
    const pMap = new Map((profs || []).map((p: any) => [p.user_id, p]));

    setConflicts(
      hits
        .map(({ b, reasonLabel }) => ({
          ...b,
          reasonLabel,
          client_name: pMap.get(b.user_id)?.full_name || "Client",
          email: pMap.get(b.user_id)?.email ?? null,
        }))
        .sort((a, b) => a.booking_date.localeCompare(b.booking_date)),
    );
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openPreview = async (c: Conflict) => {
    setPreviewFor(c);
    setLoadingPreview(true);
    setPreviewHtml(null);
    const { data, error } = await supabase.functions.invoke("send-reschedule-request", {
      body: {
        mode: "preview",
        booking_id: c.id,
        client_name: c.client_name,
        service_name: c.service_name,
        date_label: fmtBusinessDate(c.booking_date, {
          weekday: "long", month: "long", day: "numeric", year: "numeric",
        }),
        time_label: c.booking_time,
      },
    });
    setLoadingPreview(false);
    if (error || !data?.html) {
      toast.error("Couldn't load the email preview");
      return;
    }
    setPreviewHtml(data.html);
  };

  const sendEmail = async (c: Conflict) => {
    if (!c.email) {
      toast.error("This client has no email on file");
      return;
    }
    setSendingId(c.id);
    const { data, error } = await supabase.functions.invoke("send-reschedule-request", {
      body: {
        mode: "send",
        booking_id: c.id,
        email: c.email,
        client_name: c.client_name,
        service_name: c.service_name,
        date_label: fmtBusinessDate(c.booking_date, {
          weekday: "long", month: "long", day: "numeric", year: "numeric",
        }),
        time_label: c.booking_time,
      },
    });
    setSendingId(null);
    if (error || !data?.ok) {
      toast.error("Failed to send the reschedule email");
      return;
    }
    setSentIds((prev) => [...prev, c.id]);
    toast.success(`Reschedule email sent to ${c.client_name}`);
  };

  /** Admin decides to honour the appointment even though the day is blocked. */
  const allowBooking = async (c: Conflict) => {
    setAllowingId(c.id);
    const { error } = await supabase
      .from("bookings")
      .update({ blocked_day_override: true })
      .eq("id", c.id);
    setAllowingId(null);
    if (error) {
      toast.error("Couldn't approve this appointment");
      return;
    }
    setConflicts((prev) => prev.filter((x) => x.id !== c.id));
    if (previewFor?.id === c.id) {
      setPreviewFor(null);
      setPreviewHtml(null);
    }
    toast.success(`${c.client_name}'s appointment will be kept`);
  };

  if (conflicts.length === 0) return null;


  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="w-full rounded-none border border-destructive/50 bg-destructive/10 px-4 py-3 flex items-center justify-center gap-2 text-destructive animate-pulse hover:bg-destructive/20 transition-colors"
      >
        <AlertTriangle size={16} className="shrink-0" />
        <span className="text-xs sm:text-sm tracking-[0.15em] uppercase font-medium">
          {conflicts.length} appointment{conflicts.length !== 1 ? "s" : ""} booked on a blocked day — tap to reschedule
        </span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle size={18} /> Blocked-day appointments
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground">
            These upcoming appointments fall on a date or time the studio is closed. Send the client a
            reschedule request — or keep the appointment and honour it anyway.
          </p>
          <div className="space-y-3">
            {conflicts.map((c) => (
              <div key={c.id} className="rounded-md border border-destructive/25 bg-destructive/5 p-3 space-y-2">
                <div>
                  <p className="text-sm font-medium">
                    {fmtBusinessDate(c.booking_date, { weekday: "short", month: "short", day: "numeric" })} · {c.booking_time}
                  </p>
                  <p className="text-xs text-muted-foreground">{c.client_name} — {c.service_name}</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">
                    Booked on{" "}
                    {c.created_at
                      ? new Date(c.created_at).toLocaleString("en-US", {
                          timeZone: "America/New_York",
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                          hour: "numeric",
                          minute: "2-digit",
                        })
                      : "unknown date"}
                  </p>
                  <p className="text-[11px] uppercase tracking-widest text-destructive mt-1">{c.reasonLabel}</p>
                  {!c.email && <p className="text-[11px] text-muted-foreground mt-1">No email on file</p>}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="flex-1 gap-1.5 text-xs" onClick={() => openPreview(c)}>
                    <Eye size={13} /> Preview email
                  </Button>
                  <Button
                    size="sm"
                    className="flex-1 gap-1.5 text-xs"
                    disabled={!c.email || sendingId === c.id || sentIds.includes(c.id)}
                    onClick={() => sendEmail(c)}
                  >
                    {sendingId === c.id ? <Loader2 size={13} className="animate-spin" /> : <Mail size={13} />}
                    {sentIds.includes(c.id) ? "Sent" : "Send request"}
                  </Button>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full gap-1.5 text-xs text-muted-foreground hover:text-foreground"
                  disabled={allowingId === c.id}
                  onClick={() => allowBooking(c)}
                >
                  {allowingId === c.id ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />}
                  Allow this booking — keep it as scheduled
                </Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!previewFor} onOpenChange={(v) => { if (!v) { setPreviewFor(null); setPreviewHtml(null); } }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Email preview — {previewFor?.client_name}</DialogTitle>
          </DialogHeader>
          {loadingPreview && (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="animate-spin text-muted-foreground" />
            </div>
          )}
          {previewHtml && (
            <iframe
              title="Reschedule email preview"
              srcDoc={previewHtml}
              className="w-full flex-1 min-h-[420px] border border-border rounded-md bg-white"
            />
          )}
          <div className="flex gap-2">
            <Button variant="outline" className="flex-1" onClick={() => { setPreviewFor(null); setPreviewHtml(null); }}>
              Close
            </Button>
            <Button
              className="flex-1"
              disabled={!previewFor?.email || sendingId === previewFor?.id}
              onClick={() => previewFor && sendEmail(previewFor)}
            >
              Send this email
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BlockedDayConflictsBar;
